import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { PrismLogo } from './PrismLogo'
import { Box, Collapse, Divider, Group, Text, UnstyledButton } from '@mantine/core'
import { IconChevronDown, IconChevronRight, IconClock } from '@tabler/icons-react'
import { comingSoonNavigation, primaryNavigation, type NavItem } from './navigation'
import { isAllowlisted } from '../../api/allowlist'

interface NavItemProps extends NavItem {}

function SidebarNavLink({ name, href, icon: Icon }: NavItemProps) {
  return (
    <NavLink
      to={href}
      style={({ isActive }) => ({
        display: 'block',
        padding: '8px 12px',
        fontSize: '12px',
        fontWeight: 500,
        borderRadius: '6px',
        marginBottom: '4px',
        textDecoration: 'none',
        backgroundColor: isActive
          ? 'var(--mantine-color-prism-0)'
          : 'transparent',
        color: isActive
          ? 'var(--mantine-color-prism-7)'
          : 'var(--mantine-color-gray-6)',
        borderRight: isActive
          ? '2px solid var(--mantine-color-prism-7)'
          : 'none',
        transition: 'all 0.2s ease',
      })}
    >
      {({ isActive }) => (
        <Group gap="xs">
          <Icon
            size={16}
            color={
              isActive
                ? 'var(--mantine-color-prism-7)'
                : 'var(--mantine-color-gray-6)'
            }
          />
          <Text size="sm" fw={500} c={isActive ? 'prism' : 'gray.6'}>
            {name}
          </Text>
        </Group>
      )}
    </NavLink>
  )
}

export function Sidebar() {
  const [comingSoonOpen, setComingSoonOpen] = useState(false)
  const allowed = isAllowlisted()
  const visiblePrimary = allowed
    ? primaryNavigation
    : primaryNavigation.filter((item) => !item.requiresDatabricks)

  return (
    <Box
      w={256}
      bg="white"
      style={{ borderRight: '1px solid var(--mantine-color-gray-3)' }}
    >
      <Box
        h={64}
        bg="#2a0050"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid #410161',
          padding: '8px 12px',
        }}
      >
        <NavLink
          to="/dashboard"
          aria-label="Go to dashboard"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <PrismLogo
            style={{
              width: '80%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </NavLink>
      </Box>

      <nav style={{ flex: 1, padding: '24px 12px' }}>
        {visiblePrimary.map((item) => (
          <SidebarNavLink key={item.name} {...item} />
        ))}

        <Divider my="sm" color="gray.3" />

        <Box>
          <UnstyledButton
            onClick={() => setComingSoonOpen((open) => !open)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: '8px 12px',
              borderRadius: '6px',
              marginBottom: '4px',
            }}
            aria-expanded={comingSoonOpen}
          >
            <Group gap="xs">
              <IconClock size={16} color="var(--mantine-color-gray-6)" />
              <Text size="sm" fw={500} c="gray.6">
                Coming Soon
              </Text>
            </Group>
            {comingSoonOpen ? (
              <IconChevronDown size={16} color="var(--mantine-color-gray-6)" />
            ) : (
              <IconChevronRight size={16} color="var(--mantine-color-gray-6)" />
            )}
          </UnstyledButton>
          <Collapse in={comingSoonOpen}>
            {comingSoonNavigation.map((item) => (
              <SidebarNavLink key={item.name} {...item} />
            ))}
          </Collapse>
        </Box>
      </nav>
    </Box>
  )
}
